import { useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";
import { Outlet, ScrollRestoration, useLocation, useMatches, useNavigate } from "react-router";
import { closePopouts } from "../components/PopoutTooltip";
import { errorPageState } from "../routes/errors";
import { MAIN_BODY_CLASS, matchedHandle } from "./handles";
import { useHydrated, useSession } from "./session";
import { Toasts } from "./Toasts";

export const SITE_NAME = "Card Game Academy";

/** "{title} - Card Game Academy", as _Layout writes it. */
export function pageTitle(title: string | undefined): string {
    return title ? `${title} - ${SITE_NAME}` : SITE_NAME;
}

function setHeadLink(rel: string, href: string | null): void {
    let link = document.head.querySelector<HTMLLinkElement>(`link[rel='${rel}']`);

    if (href == null) {
        link?.remove();
        return;
    }

    if (link == null) {
        link = document.createElement("link");
        link.rel = rel;
        document.head.append(link);
    }

    link.href = href;
}

function setMetaDescription(content: string | undefined): void {
    let meta = document.head.querySelector<HTMLMetaElement>("meta[name='description']");

    if (content == null) {
        meta?.remove();
        return;
    }

    if (meta == null) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.append(meta);
    }

    meta.content = content;
}

/**
 * After an in-app navigation: the title, description and canonical link of the new page (the shell wrote the first
 * page's), and focus on its heading, so a screen reader announces the new page.
 */
function useRouteHead(): void {
    const location = useLocation();
    const matches = useMatches();
    const isFirstRender = useRef(true);
    // The site's main host, from the canonical link the shell wrote (when the first page had one). Not read while
    // rendering: prerendering runs without window.
    const canonicalOrigin = useRef<string | null>(null);

    const handle = matchedHandle(matches);
    // The canonical address is the route's own path, in its own case, on the host the shell's link names.
    const canonicalPath = handle.prerender ? handle.path ?? null : null;

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            const shellLink = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
            if (shellLink != null)
                canonicalOrigin.current = new URL(shellLink.href).origin;
            return;
        }

        document.title = pageTitle(handle.title);
        setMetaDescription(handle.description);

        setHeadLink("canonical", canonicalPath == null ? null : (canonicalOrigin.current ?? window.location.origin) + canonicalPath);

        // A page writing its own state into the address (Replay Analysis' trick reached) keeps the focus where it is
        const keepFocus = (location.state as { keepFocus?: boolean } | null)?.keepFocus === true;
        const heading = keepFocus ? null : document.querySelector<HTMLElement>("#app h1");
        if (heading != null) {
            if (!heading.hasAttribute("tabindex"))
                heading.setAttribute("tabindex", "-1");
            heading.focus({ preventScroll: true });
        }
        // Once per navigation; the values above belong to the new location.
    }, [location.key]);
}

/**
 * What a page load used to reset, for the new page: the body's class (the shell wrote the first page's) and an open
 * popout (the navbar's dropdown stays mounted). The board frame unfolds the navbar itself as it goes.
 */
function usePageFrame(): void {
    const location = useLocation();
    const bodyClass = matchedHandle(useMatches()).bodyClass ?? MAIN_BODY_CLASS;

    useLayoutEffect(() => {
        document.body.className = bodyClass;
    }, [bodyClass]);

    const shownKey = useRef(location.key);

    useLayoutEffect(() => {
        if (location.key !== shownKey.current) {
            shownKey.current = location.key;
            closePopouts();
        }
    }, [location.key]);
}

/**
 * A page a signed-in visitor doesn't see (the landing page, Login, Register): the app sends them on, with the error
 * page's message if the page has one, as the server does on a page load. After hydrating, since the session is the
 * signed-out one until then. Only on arriving at the page: signing in on the login page goes where the login says.
 */
function useSignedInRedirect(): void {
    const handle = matchedHandle(useMatches());
    const { isSignedIn } = useSession();
    const hydrated = useHydrated();
    const location = useLocation();
    const navigate = useNavigate();

    const onArrival = useEffectEvent(() => {
        if (handle.signedInRedirect != null && isSignedIn) {
            const message = handle.signedInMessage;
            void navigate(handle.signedInRedirect, { replace: true, state: message && errorPageState(message.title, message.message) });
        }
    });

    useEffect(() => {
        if (hydrated)
            onArrival();
    }, [hydrated, location.key]);
}

/** Admin Insights' beacon (docs/admin-insights-plan.md): an in-app navigation, which the server records as a page view. */
export const PAGE_VIEW_URL = "/api/insights/view";

/**
 * Tells the server about a page the app has shown. Fire and forget: it outlives the page (keepalive), and a failure
 * (offline, a refused request, no fetch at all) loses the view, never anything else. No antiforgery token: the server
 * takes the beacon without one.
 */
function sendPageView(path: string): void {
    try {
        fetch(PAGE_VIEW_URL, {
            method: "POST",
            keepalive: true,
            headers: { "Content-Type": "application/json" },
            // The viewport's width in CSS pixels, for later viewport buckets (unused so far)
            body: JSON.stringify({ path, screenWidth: window.innerWidth }),
        }).catch(() => {});
    }
    catch {
        // fetch itself threw: the view is lost, the page isn't
    }
}

/**
 * Admin Insights' page views after the first (the shell counted that one, as it counts every page load): one each time
 * the app shows another path. Only the path counts: a change of query or fragment on the same page (a results date's
 * tab, the hand solver's cards played) isn't a new view. Nor is a page that sends the visitor straight on, as the server
 * records no redirect: a page for signed-in visitors while signed out (RequireAuth's login), a signed-in visitor's
 * landing page, Login or Register (useSignedInRedirect), an admin page the visitor no longer may see (RequireAdmin loads
 * it in full, and the server records that load); where they land is counted. Sent from an effect, so never while
 * prerendering, and once per path however often React runs the effect (strict mode runs it twice on mounting).
 */
function usePageViews(): void {
    const { pathname } = useLocation();
    const handle = matchedHandle(useMatches());
    const { isSignedIn, isAdmin } = useSession();
    // The path last shown, counted or passed through; at first the shell's page
    const shownPath = useRef(pathname);

    const passesThrough = (handle.requiresSignIn === true && !isSignedIn)
        || (handle.signedInRedirect != null && isSignedIn)
        || (handle.requiresAdmin === true && !isAdmin);

    useEffect(() => {
        if (pathname === shownPath.current)
            return;

        shownPath.current = pathname;

        if (!passesThrough)
            sendPageView(pathname);
        // Once per path; whether it passes through belongs to the path just arrived at
    }, [pathname]);
}

/** The top of the route tree: page titles, focus and scroll position across navigations, the toasts, and Insights' page views. */
export function AppRoot() {
    useRouteHead();
    usePageFrame();
    useSignedInRedirect();
    usePageViews();

    return (
        <>
            <ScrollRestoration />
            <Outlet />
            <Toasts />
        </>
    );
}
